<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";

export default {
    name: "ApprTemp2",
    components: {
        SpellCheckedText
    },
    props: {
        questions: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
        startPage: { type: Number, default: 1 },
    },
    data() {
        return {
            pages: []
        };
    },
    watch: {
        questions: {
            deep: true,
            handler() {
                this.$nextTick(async () => {
                    await this.splitContentIntoPages();
                });
            }
        }
    },
    mounted() {
        this.$nextTick(async () => {
            await this.splitContentIntoPages();
        });
    },
    methods: {
        waitForImages(el) {
            const images = el.querySelectorAll("img");
            const promises = Array.from(images).map((img) => {
                if (img.complete) return Promise.resolve();
                return new Promise((resolve) => {
                    img.onload = img.onerror = resolve;
                });
            });
            return Promise.all(promises);
        },
        async splitContentIntoPages() {
            const availableHeight = 990;
            const colWidth = 757.46;

            const container = document.createElement("div");
            container.style.position = "absolute";
            container.style.top = "-9999px";
            container.style.left = "-9999px";
            container.style.visibility = "hidden";
            container.style.boxSizing = "border-box";
            container.style.width = `${colWidth}px`;
            container.style.padding = "0px";
            container.style.overflowWrap = "break-word";
            container.style.wordBreak = "break-word";

            const styleTag = document.createElement("style");
            styleTag.innerHTML = `
                img { max-width: 100%; height: auto; display: inline-block; vertical-align: middle; margin: 10px 0px; }
                p, ul, ol, li { margin: 0px !important; }
            `;
            document.head.appendChild(styleTag);
            document.body.appendChild(container);

            let itemQueue = JSON.parse(JSON.stringify(this.questions.questions || []));
            this.pages = [];
            container.innerHTML = "";

            const headingStyle = `font-family: ${this.questions.style.heading.font}; font-size: ${this.questions.style.heading.size}px; font-weight: ${this.questions.style.heading.bold ? "bold" : "normal"}; color: ${this.questions.style.heading.color}; margin-bottom: 20px; break-inside: avoid; word-break: break-word; overflow-wrap: break-word; line-break: auto; line-height: 1.2;`;
            const textStyle = `font-family: ${this.questions.style.text.font}; font-size: ${this.questions.style.text.size}px; font-weight: ${this.questions.style.text.bold ? "bold" : "normal"}; color: ${this.questions.style.text.color}; margin-bottom: 0px; display: block; word-break: break-word; overflow-wrap: break-word; line-break: auto; break-inside: auto; line-height: 1.4;`;

            const fillPage = async () => {
                const colItems = [];
                let currentHeight = 0;
                const sectionMargin = 20;
                let iterations = 0;
                const MAX_ITERATIONS = 100;

                while (itemQueue.length > 0 && iterations < MAX_ITERATIONS) {
                    iterations++;
                    const item = itemQueue[0];
                    const section = document.createElement("div");

                    let headingHtml = "";
                    if (item.heading) {
                        headingHtml = `<h2 style="${headingStyle}">${item.heading}</h2>`;
                    }
                    section.innerHTML = `${headingHtml}<div style="${textStyle}">${item.response}</div>`;
                    container.appendChild(section);
                    await this.waitForImages(section);

                    if (currentHeight + section.scrollHeight + sectionMargin <= availableHeight) {
                        colItems.push(item);
                        currentHeight += section.scrollHeight + sectionMargin;
                        itemQueue.shift();
                    } else {
                        container.removeChild(section);

                        const headingDiv = document.createElement("div");
                        if (item.heading) {
                            headingDiv.innerHTML = `<h2 style="${headingStyle}">${item.heading}</h2>`;
                        }
                        container.appendChild(headingDiv);

                        const hH = item.heading ? headingDiv.scrollHeight : 0;
                        if (currentHeight + hH + sectionMargin <= availableHeight) {
                            if (!item.heading) container.removeChild(headingDiv);

                            const tempDiv = document.createElement("div");
                            tempDiv.style.cssText = textStyle;
                            container.appendChild(tempDiv);

                            const parser = new DOMParser();
                            const doc = parser.parseFromString(item.response, 'text/html');
                            const childNodes = Array.from(doc.body.childNodes);

                            let validHtml = "";
                            let remainderHtml = "";

                            for (let i = 0; i < childNodes.length; i++) {
                                const node = childNodes[i].cloneNode(true);
                                tempDiv.appendChild(node);
                                await this.waitForImages(tempDiv);
                                if (currentHeight + hH + tempDiv.scrollHeight + sectionMargin > availableHeight) {
                                    if (!validHtml && currentHeight === 0) {
                                        validHtml = tempDiv.innerHTML;
                                        for (let j = i + 1; j < childNodes.length; j++) {
                                            const wrapper = document.createElement("div");
                                            wrapper.appendChild(childNodes[j].cloneNode(true));
                                            remainderHtml += wrapper.innerHTML;
                                        }
                                    } else {
                                        for (let j = i; j < childNodes.length; j++) {
                                            const wrapper = document.createElement("div");
                                            wrapper.appendChild(childNodes[j].cloneNode(true));
                                            remainderHtml += wrapper.innerHTML;
                                        }
                                    }
                                    break;
                                }
                                validHtml = tempDiv.innerHTML;
                            }

                            if (validHtml) {
                                const finalH = hH + tempDiv.scrollHeight + sectionMargin;
                                container.innerHTML = "";
                                colItems.push({
                                    heading: item.heading,
                                    response: validHtml,
                                    _isContinuation: item._isContinuation || false
                                });
                                currentHeight += finalH;
                                itemQueue.shift();
                                if (remainderHtml) {
                                    itemQueue.unshift({
                                        ...item,
                                        heading: "",
                                        response: remainderHtml,
                                        _isContinuation: true
                                    });
                                }
                                return colItems;
                            } else {
                                container.innerHTML = "";
                                return colItems;
                            }
                        } else {
                            container.innerHTML = "";
                            break;
                        }
                    }
                }
                container.innerHTML = "";
                return colItems;
            };

            while (itemQueue.length > 0) {
                this.pages.push(await fillPage());
            }

            document.body.removeChild(container);
            document.head.removeChild(styleTag);
        }
    }
};
</script>

<template>
  <div>
    <div class="main-container" :style="{
      gap: pageGap ? '20px' : '0px',
      '--header-color': questions.style.colorScheme.header,
      '--body-color': questions.style.colorScheme.body,
      '--footer-color': questions.style.colorScheme.footer,
      '--heading-color': questions.style.heading.color,
      '--heading-size': questions.style.heading.size + 'px',
      '--heading-weight': questions.style.heading.bold ? 'bold' : 'normal',
      '--heading-style': questions.style.heading.italic ? 'italic' : 'normal',
      '--heading-decoration': questions.style.heading.underline
        ? 'underline'
        : 'none',
      '--heading-font': questions.style.heading.font,
      '--label-color': questions.style.label.color,
      '--label-size': questions.style.label.size + 'px',
      '--label-weight': questions.style.label.bold ? 'bold' : 'normal',
      '--label-style': questions.style.label.italic ? 'italic' : 'normal',
      '--label-decoration': questions.style.label.underline
        ? 'underline'
        : 'none',
      '--label-font': questions.style.label.font,
      '--text-color': questions.style.text.color,
      '--text-size': questions.style.text.size + 'px',
      '--text-weight': questions.style.text.bold ? 'bold' : 'normal',
      '--text-style': questions.style.text.italic ? 'italic' : 'normal',
      '--text-decoration': questions.style.text.underline
        ? 'underline'
        : 'none',
      '--text-font': questions.style.text.font,
    }">
      <div class="sub-container page-unit" v-for="(page, pageIndex) in pages" :key="pageIndex">
        <div class="banner header-banner">
          <h2>{{ questions.title }}</h2>
        </div>

        <div class="page-container">
          <div class="project-info text-left">
            <div v-for="(item, index) in page" :key="index" class="section">
              <h2 v-if="item.heading" class="heading">
                <SpellCheckedText :value="item.heading" @input="item.heading = $event" />
              </h2>
              <div class="desc">
                <SpellCheckedText :value="item.response" @input="item.response = $event" />
              </div>
            </div>
          </div>
        </div>
        <div class="banner footer-banner">
          <span class="page-number">{{ String(startPage + pageIndex).padStart(2, '0') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.main-container {
  display: flex;
  flex-direction: column;
  width: 211mm;
  min-height: 296.83mm;
  position: relative;
  box-sizing: border-box;
  background: white;

  .sub-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 296.83mm;

    .banner {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 60px;
      position: relative;

      &.header-banner {
        background: var(--header-color);
      }

      &.footer-banner {
        background: var(--footer-color);
      }

      h2 {
        font-size: 18px;
        color: white;
        margin-bottom: 0px;
        font-weight: 700;
        font-family: Helvetica;
      }

      .page-number {
        position: absolute;
        right: 15px;
        bottom: 10px;
        font-size: 14px;
        font-weight: 700;
        color: white;
        font-family: Helvetica;
      }
    }

    .page-container {
      padding: 20px;
      height: 100%;
      overflow: hidden;

      .project-info {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        max-width: 100%;

        .section {
          break-inside: avoid;
          overflow-wrap: anywhere;
          word-break: break-word;
          max-width: 100%;
        }

        .heading {
          break-inside: avoid;
          break-after: avoid;
          margin-bottom: 20px;
          word-break: break-word;
          overflow-wrap: break-word;
          color: var(--heading-color);
          font-size: var(--heading-size);
          font-weight: var(--heading-weight);
          font-family: var(--heading-font);
          font-style: var(--heading-style);
          text-decoration: var(--heading-decoration);
          line-height: 1.2;
        }

        .desc {
          display: block;
          word-break: break-word;
          overflow-wrap: break-word;
          white-space: normal;
          line-break: auto;
          margin-bottom: 20px;
          hyphens: auto;
          line-height: 1.4;
          color: var(--text-color);
          font-size: var(--text-size);
          font-weight: var(--text-weight);
          font-family: var(--text-font);
          font-style: var(--text-style);
          text-decoration: var(--text-decoration);
        }
      }
    }
  }
}
</style>